import { GetQuoteExpired } from '../../generated/rating-quoting-graphql';
import { getEarliestQuoteExpireDate } from './effectiveDateRules';
import { QuotePagePurchaseState } from './useQuotePageRedirect';

// MET-468: what the quote page shows when it stays put and a quote came back
// expired. Routing stays in useQuotePageRedirect.ts.

export type EffectiveDateAutoUpdateStatus =
  | 'idle'
  | 'updating'
  | 'refreshing'
  | 'succeeded'
  | 'skipped'
  | 'failed';

export interface EffectiveDateAutoUpdateState {
  status: EffectiveDateAutoUpdateStatus;
  /** The lapsed date, read from getBusinessInfo before the write. */
  previousCoverageStartDate?: string;
  isBannerDismissed: boolean;
  /** The customer picked a date in the editor after the auto-update. */
  isSupersededByManualChange: boolean;
}

export const INITIAL_EFFECTIVE_DATE_AUTO_UPDATE_STATE: EffectiveDateAutoUpdateState = {
  status: 'idle',
  isBannerDismissed: false,
  isSupersededByManualChange: false
};

export type ExpiredEffectiveDateAction = 'autoUpdate' | 'chooseDatePopup';

export type ExpiredQuoteScreen =
  | { kind: 'none' }
  | { kind: 'loading' }
  | { kind: 'autoUpdate' }
  | { kind: 'deadEnd'; quoteExpireDate: string }
  | { kind: 'chooseDatePopup'; quoteExpireDate: string };

/** True only when every expired response is an effective-date-only expiry. */
export function isGroupOnlyEffectiveDateExpired(
  expired: GetQuoteExpired[]
): boolean {
  return (
    expired.length > 0 &&
    expired.every(
      (r) =>
        !!r.isEffectiveDateExpired &&
        !r.isQuoteExpired &&
        !r.isGracePeriodExpired &&
        !!r.quoteExpireDate
    )
  );
}

export function getExpiredEffectiveDateAction({
  purchaseState
}: {
  purchaseState: QuotePagePurchaseState | undefined;
}): ExpiredEffectiveDateAction {
  if (!purchaseState) return 'chooseDatePopup';
  if (!purchaseState.hasGroupPaidFlags) return 'chooseDatePopup';
  if (purchaseState.isGroupPartiallyPaid || purchaseState.isPaid) {
    return 'chooseDatePopup';
  }

  return purchaseState.transactionType === 'New Business'
    ? 'autoUpdate'
    : 'chooseDatePopup';
}

/**
 * Re-check against fresh group state before the write; fails closed. Paid
 * blocks; signed no longer needs its own branch (see
 * getExpiredEffectiveDateAction) — any other state auto-updates.
 */
export function confirmAutoUpdateWithGroupState(
  groupState: { isSigned: boolean; isPaid: boolean } | null | undefined
): ExpiredEffectiveDateAction {
  if (!groupState || groupState.isPaid) return 'chooseDatePopup';
  return 'autoUpdate';
}

export function decideExpiredQuoteScreen({
  expiredQuoteResponses,
  purchaseState,
  isRedirectCheckComplete,
  autoUpdateStatus,
  canAutoUpdate
}: {
  expiredQuoteResponses: GetQuoteExpired[];
  purchaseState: QuotePagePurchaseState | undefined;
  isRedirectCheckComplete: boolean;
  autoUpdateStatus: EffectiveDateAutoUpdateStatus;
  canAutoUpdate: boolean;
}): ExpiredQuoteScreen {
  if (expiredQuoteResponses.length === 0) return { kind: 'none' };
  if (!isRedirectCheckComplete) return { kind: 'loading' };

  // Any whole-quote or grace-period expiry in the group is a dead end.
  const deadEnd = expiredQuoteResponses.find(
    (r) => r.isQuoteExpired || r.isGracePeriodExpired
  );
  if (deadEnd) {
    return deadEnd.quoteExpireDate
      ? { kind: 'deadEnd', quoteExpireDate: deadEnd.quoteExpireDate }
      : { kind: 'loading' };
  }
  if (!isGroupOnlyEffectiveDateExpired(expiredQuoteResponses)) {
    return { kind: 'loading' };
  }

  const fallbackDate = getEarliestQuoteExpireDate(
    expiredQuoteResponses.map((r) => r.quoteExpireDate)
  ) as string;
  const chooseDatePopup: ExpiredQuoteScreen = {
    kind: 'chooseDatePopup',
    quoteExpireDate: fallbackDate
  };

  if (
    getExpiredEffectiveDateAction({ purchaseState }) === 'chooseDatePopup' ||
    !canAutoUpdate
  ) {
    return chooseDatePopup;
  }

  switch (autoUpdateStatus) {
    case 'idle':
      return { kind: 'autoUpdate' };
    case 'updating':
    case 'refreshing':
      return { kind: 'loading' };
    default:
      // Already attempted: no retry, fall back to the date-choice popup.
      return chooseDatePopup;
  }
}

export function getEffectiveDateNotice(
  state: EffectiveDateAutoUpdateState
):
  | {
      previousCoverageStartDate?: string;
      showBanner: boolean;
      showUpdatedNote: boolean;
    }
  | undefined {
  if (state.status !== 'succeeded') return undefined;
  return {
    previousCoverageStartDate: state.previousCoverageStartDate,
    showBanner: !state.isBannerDismissed && !state.isSupersededByManualChange,
    showUpdatedNote: !state.isSupersededByManualChange
  };
}
